import Card from '../../components/ui/card/Card'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import apolloClient from '../../apollo-client'
import NewsFilters, {
  NewsFilterRefType
} from '../../components/filters/NewsFilter/NewsFilters'
import PageHeader from '../../components/pageHeader/PageHeader'
import { GET_NEWS } from '../../graphql/news'
import { limitText, shuffleArray } from '../../lib/Utils'
import { NewsListItem } from '../../types/News'
import DefaultHero from '../../public/assets/img/company/default.webp'
import { LoadMask } from '../../components/ui/LoadMask'
import { GET_JOB_POSTS } from '../../graphql/jobPost'
import { JobPostItem } from '../../types/JobPost'
import JobCard from '../../components/ui/card/JobCard'
import NoResult from '../../components/ui/NoResult'
import NewsListHead from '../../components/heads/NewsListHead'
import GlobalContext from '../../contexts/GlobalContext'

const LIMIT = 9
export default function NewsSearch() {
  const [topJobPost, setTopJobPost] = useState<JobPostItem[]>([])
  const [news, setNews] = useState<NewsListItem[]>([])
  const [loading, setLoading] = useState(false)
  const [topJobsLoading, setTopJobsLoading] = useState(false)
  const [totalCnt, setTotalCnt] = useState(0)
  const { setErrorMsg } = useContext(GlobalContext)
  const filterRef = useRef<NewsFilterRefType>(null)

  const pageSize = useRef(0)

  useEffect(() => {
    const load = async () => {
      try {
        setTopJobsLoading(true)
        const { data } = await apolloClient.query<any>({
          query: GET_JOB_POSTS,
          variables: {
            query: {
              active: true,
              feed: false,
              isPublished: true,
              topJobPost: 3,
              sort: { active: 'campaignPackageWeight', direction: 'desc' }
            }
          }
        })
        setTopJobPost(data.jobPosts.items)
      } catch {
        setErrorMsg('Uh-oh! Error loading featured jobs. Try again soon.')
      } finally {
        setTopJobsLoading(false)
      }
    }
    load()
  }, [setErrorMsg])

  const filterNews = useCallback(
    async (reset = false) => {
      try {
        if (reset) {
          setNews([])
          pageSize.current = 0
        }
        setLoading(true)

        const { data } = await apolloClient.query<any>({
          fetchPolicy: 'network-only',
          query: GET_NEWS,
          variables: {
            query: {
              published: true,
              ...(filterRef.current?.getValues() || {}),
              skip: pageSize.current,
              limit: LIMIT
            }
          }
        })

        const items = data.news.items as NewsListItem[]
        const newData = [
          ...items.filter((item) => item.pinned),
          ...shuffleArray(items.filter((item) => !item.pinned))
        ]
        if (reset) {
          setNews(newData)
        } else {
          setNews((oldNews) => [...oldNews, ...newData])
        }

        setTotalCnt(data.news.totalCount)
        pageSize.current = pageSize.current + LIMIT
      } catch {
        setErrorMsg('Uh-oh! Error filtering news. Try again soon.')
      } finally {
        setLoading(false)
      }
    },
    [setErrorMsg]
  )

  useEffect(() => {
    const loadNews = () => {
      filterNews()
    }

    loadNews()
  }, [filterNews])

  const noResult = () => {
    if (news.length === 0 && !loading) return <NoResult visible={true} />
  }

  return (
    <>
      <NewsListHead />
      <PageHeader
        bucket="truck-drivers-news/hero"
        isHold={false}
        enableHide={false}
      />
      <NewsFilters
        ref={filterRef}
        onSearch={() => {
          filterNews(true)
        }}
      />
      <div className="max-w-[1200px] mx-auto grid grid-cols-1 lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-1">
        {topJobPost.map((item, i) => {
          return <JobCard item={item} key={i}></JobCard>
        })}
      </div>

      <InfiniteScroll
        dataLength={news.length}
        scrollableTarget="scrollableTarget"
        loader={null}
        style={{ position: 'relative' }}
        next={() => {
          filterNews()
        }}
        hasMore={totalCnt > pageSize.current}>
        <LoadMask active={loading || topJobsLoading}></LoadMask>

        <div className="max-w-[1200px] mx-auto grid grid-cols-1 lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-1">
          {news.map((item, i) => {
            return (
              <Card
                className="cursor-pointer  border-transparent border-4 hover:border-[#edbb4c] news-card"
                image={item?.image?.Location || DefaultHero}
                title={item.title}
                key={i}
                overview={limitText(item.body, 240)}
                href={`/truck-drivers-news/${item._id}`}
                hideFooter={true}></Card>
            )
          })}
        </div>
        <div className="flex justify-center items-center">{noResult()}</div>
      </InfiniteScroll>
    </>
  )
}
